import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import { resolveFileUrl, authenticatedFetch, API_URL } from '../../lib/api';

type LocationState = {
  blobUrl?: string;
  fileName?: string;
  fileType?: string;
  returnTo?: string;
};

const isImage = (name: string, mime?: string) =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name) || (mime || '').startsWith('image/');

const isPdf = (name: string, mime?: string) =>
  /\.pdf$/i.test(name) || (mime || '').includes('pdf');

export function DocumentPreviewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = (location.state || {}) as LocationState;

  const rawUrl = searchParams.get('url') || '';
  const fileName = searchParams.get('name') || state.fileName || 'Document';
  const fileType = searchParams.get('type') || state.fileType || '';
  const returnTo =
    searchParams.get('returnTo') || state.returnTo || '/dashboard/admin/rfqs/new';
  const title = searchParams.get('title') || 'Supporting document';

  const blobFromState = state.blobUrl || '';
  const resolvedRemote = useMemo(() => resolveFileUrl(rawUrl), [rawUrl]);

  const [objectUrl, setObjectUrl] = useState(blobFromState);
  const [contentType, setContentType] = useState(fileType);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!blobFromState && !!resolvedRemote);

  useEffect(() => {
    if (blobFromState) {
      setObjectUrl(blobFromState);
      setContentType(fileType);
      setLoading(false);
      return;
    }
    if (!resolvedRemote) {
      setError('No document to preview.');
      setLoading(false);
      return;
    }

    let active = true;
    let created = '';

    const load = async () => {
      try {
        const apiBase = String(API_URL || '').replace(/\/$/, '');
        const needsAuth =
          !rawUrl.startsWith('http') ||
          resolvedRemote.startsWith(apiBase) ||
          resolvedRemote.includes('/uploads/');
        const path = needsAuth
          ? resolvedRemote.replace(apiBase, '') || resolvedRemote
          : resolvedRemote;
        const response = needsAuth
          ? await authenticatedFetch(path.startsWith('/') ? path : `/${path}`, {
              headers: { Accept: '*/*' },
            })
          : await fetch(resolvedRemote);
        if (!response.ok) throw new Error('Failed to load document.');
        let blob = await response.blob();
        const headerType = response.headers.get('content-type') || '';
        const effective =
          (headerType.startsWith('image/') || headerType.includes('pdf') ? headerType : '') ||
          fileType ||
          (isPdf(fileName) ? 'application/pdf' : '') ||
          blob.type;
        if (effective && blob.type !== effective) {
          blob = new Blob([blob], { type: effective });
        }
        if (!active) return;
        created = URL.createObjectURL(blob);
        setObjectUrl(created);
        setContentType(effective);
        setError('');
      } catch (e: any) {
        if (!active) return;
        setError(e?.message || 'Failed to load document.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [blobFromState, resolvedRemote, rawUrl, fileName, fileType]);

  // Revoke only blob URLs created from local File navigation (state), on unmount
  useEffect(() => {
    return () => {
      if (blobFromState) URL.revokeObjectURL(blobFromState);
    };
  }, [blobFromState]);

  return (
    <div className="w-full space-y-4 p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(returnTo)}
        className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
      >
        <MdArrowBack className="h-4 w-4" />
        Back
      </button>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-sm">
        <p className="mb-1 text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
          {title}
        </p>
        <h1 className="mb-4 text-xl font-semibold text-[hsl(var(--foreground))]">{fileName}</h1>

        {loading && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading document…</p>
        )}
        {error && <p className="mb-3 text-sm text-rose-600">{error}</p>}

        {!loading && objectUrl && isImage(fileName, contentType) && (
          <img
            src={objectUrl}
            alt={fileName}
            className="max-h-[75vh] w-auto rounded-lg border border-[hsl(var(--border))]"
          />
        )}
        {!loading && objectUrl && isPdf(fileName, contentType) && (
          <iframe
            title={fileName}
            src={objectUrl}
            className="h-[75vh] w-full rounded-lg border border-[hsl(var(--border))]"
          />
        )}
        {!loading && objectUrl && !isImage(fileName, contentType) && !isPdf(fileName, contentType) && (
          <a
            href={objectUrl}
            download={fileName}
            className="text-sm font-semibold text-[#0088cc] hover:underline"
          >
            Download file
          </a>
        )}
      </div>
    </div>
  );
}
